import { useAccount, useConnect, useDisconnect, useSwitchChain, useBalance } from "wagmi";
import { formatEther } from "viem";
import { Link } from "react-router-dom";
import { Wallet, LogOut, ShieldCheck, KeyRound, Network, Loader2, UserRound, ExternalLink, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useApp } from "../context/AppContext";
import { BACKEND } from "../lib/api";
import { short, fmtNum } from "../lib/format";
import { robinhood, robinhoodTestnet, WC_PROJECT_ID } from "../lib/chains";

const DEEP_LINKS = [
  ["MetaMask", (u) => `https://metamask.app.link/dapp/${u.replace(/^https?:\/\//, "")}`],
  ["Coinbase Wallet", (u) => `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(u)}`],
  ["Trust Wallet", (u) => `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(u)}`],
];

export const ConnectMenu = ({ testid = "navbar-connect-wallet-button" }) => {
  const { connectors, connectAsync, isPending } = useConnect();
  const { switchChainAsync } = useSwitchChain();
  const hasProvider = typeof window !== "undefined" && !!window.ethereum;
  const detected = connectors.filter((c) => c.type === "injected" && c.id !== "injected");
  const has = (re) => detected.some((c) => re.test(c.name));
  const usable = [
    ...detected,
    ...connectors.filter((c) => c.id === "injected" && hasProvider && !detected.length),
    ...connectors.filter((c) => c.type === "coinbaseWallet" && !has(/coinbase/i)),
    ...connectors.filter((c) => c.type === "walletConnect"),
  ];
  const label = (c) => (c.id === "injected" ? "Browser wallet" : c.type === "metaMask" ? "MetaMask (app or extension)" : c.type === "walletConnect" ? "WalletConnect · Robinhood Wallet & mobile" : c.name);
  const go = async (c) => {
    try {
      const res = await connectAsync({ connector: c });
      if (res.chainId !== robinhood.id) {
        try { await switchChainAsync({ chainId: robinhood.id }); }
        catch { toast.message("Connected. Switch your wallet to Robinhood Chain (4663) to swap."); }
      }
    } catch (e) {
      const m = e?.shortMessage || e?.message || "Connection failed";
      toast.error(/reject|denied/i.test(m) ? "Connection request rejected in wallet" : /already pending/i.test(m) ? "Open your wallet — a connection request is already waiting" : m);
    }
  };
  const here = typeof window !== "undefined" ? window.location.href : "";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={testid} className="btn-lime px-4 py-2 text-sm">
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />} <span className="sm:hidden">Connect</span><span className="hidden sm:inline">Connect Wallet</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-[#12161F] border-lime/20 text-slate-200">
        <DropdownMenuLabel className="text-xs text-slate-400">{detected.length || hasProvider ? "Choose a wallet" : "No browser wallet found — use an app below"}</DropdownMenuLabel>
        {usable.map((c) => (
          <DropdownMenuItem key={c.uid} data-testid={`connect-${c.id}`} onClick={() => go(c)} className="cursor-pointer gap-2">
            {c.icon ? <img src={c.icon} alt="" className="h-4 w-4" /> : <Wallet className="h-4 w-4 text-lime" />}
            {label(c)}
          </DropdownMenuItem>
        ))}
        {!WC_PROJECT_ID && <DropdownMenuItem disabled data-testid="connect-walletconnect-unconfigured" className="gap-2 text-xs"><Wallet className="h-4 w-4" /> WalletConnect — awaiting Project ID</DropdownMenuItem>}
        {!detected.length && !hasProvider && (
          <>
            <DropdownMenuSeparator className="bg-white/10" />
            <p className="px-2 pb-1 text-[11px] text-slate-400 leading-relaxed">Or open HIVEVORA inside your wallet's built-in browser:</p>
            {DEEP_LINKS.map(([n, f]) => (
              <DropdownMenuItem key={n} asChild className="cursor-pointer gap-2">
                <a data-testid={`open-in-${n.split(" ")[0].toLowerCase()}`} href={f(here)}><ExternalLink className="h-4 w-4 text-lime" /> Open in {n}</a>
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <p className="px-2 py-1.5 text-[11px] text-slate-500 leading-relaxed">Connecting never costs gas. Signing in (for chat & profile) is a free message signature. HIVEVORA never asks for seed phrases.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const WalletButton = () => {
  const { address, isConnected } = useAccount();
  const { chainId } = useAccount();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const { data: bal } = useBalance({ address, chainId: robinhood.id, query: { enabled: !!address } });
  const { user, signIn, signOut, signing, isAdmin } = useApp();

  if (!isConnected) return <ConnectMenu />;
  const wrong = ![robinhood.id, robinhoodTestnet.id].includes(chainId);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="navbar-wallet-menu" className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-mono ${wrong ? "border-danger/60 text-danger" : "border-lime/30 text-slate-100 hover:border-lime/60"} transition-colors`}>
          {user?.avatar_url ? <img src={`${BACKEND}${user.avatar_url}`} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className={`h-2 w-2 rounded-full ${user ? "bg-lime shadow-[0_0_8px_#39FF14]" : "bg-slate-500"}`} />}
          {wrong ? "Wrong network" : user?.alias || short(address)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-[#12161F] border-lime/20 text-slate-200">
        <DropdownMenuLabel className="font-mono text-xs">{address}</DropdownMenuLabel>
        <div className="px-2 pb-2 text-xs text-slate-400" data-testid="wallet-eth-balance">
          Mainnet balance: {bal ? `${fmtNum(formatEther(bal.value), 5)} ETH` : "…"}
        </div>
        <DropdownMenuSeparator className="bg-white/10" />
        {[robinhood, robinhoodTestnet].map((c) => (
          <DropdownMenuItem key={c.id} data-testid={`switch-chain-${c.id}`} disabled={chainId === c.id} onClick={() => switchChain({ chainId: c.id })} className="cursor-pointer gap-2">
            <Network className="h-4 w-4 text-lime" /> {c.name} {chainId === c.id && "· active"}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-white/10" />
        {user && (
          <DropdownMenuItem asChild className="cursor-pointer gap-2">
            <Link to={`/u/${user.address}`} data-testid="wallet-my-profile"><UserRound className="h-4 w-4 text-lime" /> My profile</Link>
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <DropdownMenuItem asChild className="cursor-pointer gap-2">
            <Link to="/admin" data-testid="wallet-admin-link" className="text-lime"><Settings2 className="h-4 w-4" /> Admin console</Link>
          </DropdownMenuItem>
        )}
        {user ? (
          <DropdownMenuItem data-testid="wallet-sign-out" onClick={signOut} className="cursor-pointer gap-2">
            <ShieldCheck className="h-4 w-4 text-lime" /> Signed in{isAdmin ? " · admin" : ""} — sign out
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem data-testid="wallet-sign-in" onClick={signIn} disabled={signing} className="cursor-pointer gap-2">
            <KeyRound className="h-4 w-4 text-lime" /> {signing ? "Check your wallet…" : "Sign in with Ethereum"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="wallet-disconnect" onClick={() => { signOut(); disconnect(); }} className="cursor-pointer gap-2 text-danger">
          <LogOut className="h-4 w-4" /> Disconnect
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
